import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { documentsAPI, policiesAPI } from 'services/api';

const AgentDocumentVerification = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [verificationComments, setVerificationComments] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all policies for document verification
      const policiesResponse = await policiesAPI.getAll();
      const policies = policiesResponse.data;

      // Load documents for each policy
      const documentPromises = policies.map(async (policy) => {
        try {
          const docsResponse = await documentsAPI.getByPolicy(policy.id);
          return { policy, documents: docsResponse.data };
        } catch (err) {
          console.warn(`Failed to load documents for policy ${policy.id}:`, err);
          return { policy, documents: [] };
        }
      });

      const documentResults = await Promise.all(documentPromises);
      setDocuments(documentResults);

    } catch (err) {
      console.error('Error loading document verification data:', err);
      setError('Failed to load document verification data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleVerifyDocument = async () => {
    if (!selectedDocument || !verificationStatus) return;

    try {
      await documentsAPI.verify(
        selectedDocument.id,
        verificationStatus === 'approved',
        verificationComments
      );

      // Reload data
      await loadData();
      setVerifyDialogOpen(false);
      setSelectedDocument(null);
      setVerificationStatus('');
      setVerificationComments('');
    } catch (err) {
      console.error('Error verifying document:', err);
      setError('Failed to verify document. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (verified) => {
    return verified ? <CheckCircle color="success" /> : <Cancel color="error" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Review and verify farmer documents for policy approval
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {documents.map(({ policy, documents: policyDocs }) => (
          <Grid item xs={12} key={policy.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Policy: {policy.policy_number}
                  </Typography>
                  <Chip 
                    label={policy.status} 
                    color={policy.status === 'active' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Sum Insured: ₹{policy.sum_insured?.toLocaleString() || 'N/A'} | 
                  Premium: ₹{policy.final_premium?.toLocaleString() || 'N/A'}
                </Typography>

                {policyDocs.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Document Type</TableCell>
                          <TableCell>File Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Comments</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {policyDocs.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell>{doc.doc_type}</TableCell>
                            <TableCell>{doc.file_name}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {getStatusIcon(doc.verified)}
                                <Chip 
                                  label={doc.verified ? 'Verified' : 'Pending'} 
                                  color={getStatusColor(doc.verified ? 'verified' : 'pending')}
                                  size="small"
                                />
                              </Box>
                            </TableCell>
                            <TableCell>{doc.comments || '-'}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setVerifyDialogOpen(true);
                                }}
                                disabled={doc.verified}
                              >
                                <Visibility />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No documents uploaded for this policy
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Verification Dialog */}
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Document</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Document:</strong> {selectedDocument.file_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Type:</strong> {selectedDocument.doc_type}
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Verification Status</InputLabel>
                <Select
                  value={verificationStatus}
                  onChange={(e) => setVerificationStatus(e.target.value)}
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments"
                value={verificationComments}
                onChange={(e) => setVerificationComments(e.target.value)}
                margin="normal"
                placeholder="Add verification comments..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleVerifyDocument}
            variant="contained"
            disabled={!verificationStatus}
          >
            Verify Document
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentDocumentVerification;
